import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const PLATFORMS = [
  { value: "zoom",         label: "Zoom" },
  { value: "teams",        label: "Microsoft Teams" },
  { value: "google_meet",  label: "Google Meet" },
  { value: "phone",        label: "Phone Call" },
  { value: "in_person",    label: "In Person" },
  { value: "other",        label: "Other" },
];

const TIMEZONES = [
  { value: "UTC",                label: "UTC" },
  { value: "America/New_York",   label: "Eastern (ET)" },
  { value: "America/Chicago",    label: "Central (CT)" },
  { value: "America/Denver",     label: "Mountain (MT)" },
  { value: "America/Los_Angeles",label: "Pacific (PT)" },
  { value: "America/Anchorage",  label: "Alaska (AT)" },
  { value: "Pacific/Honolulu",   label: "Hawaii (HT)" },
  { value: "Europe/London",      label: "London (GMT/BST)" },
  { value: "Europe/Paris",       label: "Central Europe (CET)" },
  { value: "Asia/Bangkok",       label: "Bangkok (ICT)" },
  { value: "Asia/Singapore",     label: "Singapore (SGT)" },
  { value: "Asia/Tokyo",         label: "Tokyo (JST)" },
  { value: "Australia/Sydney",   label: "Sydney (AEST)" },
];

const ACCEPTED = ".txt,.vtt,.srt,.docx,.pdf";
const EMPTY_FORM = {
  title: "", meeting_date: "", meeting_time: "",
  meeting_timezone: "UTC", source_platform: "teams", notes: "",
};

const inputCls =
  "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400";
const selectCls = `${inputCls} appearance-none cursor-pointer`;

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

/**
 * UploadTranscriptDialog
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   clientId   — string (pre-set from workspace; not shown to staff)
 *   onUploaded — () => void  (called after successful upload to trigger reload)
 */
export default function UploadTranscriptDialog({ open, onClose, clientId, onUploaded }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [duplicates, setDuplicates] = useState(null); // null = unchecked
  const fileRef = useRef(null);

  const reset = () => {
    setForm(EMPTY_FORM);
    setFile(null);
    setDuplicates(null);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => { reset(); onClose(); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doUpload = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("client_id", clientId);
      fd.append("meeting_date", form.meeting_date);
      fd.append("meeting_time", form.meeting_time);
      fd.append("meeting_timezone", form.meeting_timezone);
      fd.append("title", form.title);
      fd.append("source_platform", form.source_platform);
      if (form.notes.trim()) fd.append("notes", form.notes.trim());
      fd.append("file", file);
      await api.post("/meeting-transcripts/manual-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Transcript uploaded — Allison is analyzing it now.");
      reset();
      onClose();
      if (onUploaded) onUploaded();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed. Please try again.");
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim())      return toast.error("Meeting title is required.");
    if (!form.meeting_date)      return toast.error("Meeting date is required.");
    if (!form.meeting_time)      return toast.error("Meeting time is required.");
    if (!form.source_platform)   return toast.error("Source platform is required.");
    if (!file)                   return toast.error("Please select a transcript file.");

    // Already showed duplicate warning and staff confirmed → upload directly
    if (duplicates !== null) { await doUpload(); return; }

    setBusy(true);
    try {
      const startedAt = `${form.meeting_date}T${form.meeting_time}:00`;
      const res = await api.get(
        `/meeting-transcripts/check-duplicate?client_id=${encodeURIComponent(clientId)}&started_at=${encodeURIComponent(startedAt)}`
      );
      const dupes = res.data?.duplicates || [];
      if (dupes.length > 0) {
        setDuplicates(dupes);
        setBusy(false);
      } else {
        setDuplicates([]);
        await doUpload();
      }
    } catch {
      // Duplicate check failure is non-fatal — proceed with upload
      setDuplicates([]);
      await doUpload();
    }
  };

  const confirmDuplicate = async () => {
    setDuplicates([]); // clear warning, upload proceeds
    await doUpload();
  };

  // ── Duplicate warning screen ───────────────────────────────────────────────
  if (duplicates && duplicates.length > 0) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md" data-testid="upload-transcript-dup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Icons.AlertTriangle className="h-5 w-5" />
              Possible Duplicate Meeting
            </DialogTitle>
            <DialogDescription>
              A meeting for this client already exists at approximately this date and time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 my-2">
            {duplicates.map((d) => (
              <div key={d.id} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                <div className="font-medium text-slate-800">{d.title || "Meeting"}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {d.source_platform ? `${d.source_platform} · ` : ""}
                  {d.started_at
                    ? new Date(d.started_at).toLocaleString()
                    : d.created_at
                    ? new Date(d.created_at).toLocaleString()
                    : ""}
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-600">Continue uploading this as a separate meeting?</p>
          <div className="flex gap-2 justify-end pt-1">
            <button
              onClick={() => setDuplicates(null)}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="upload-transcript-dup-cancel">
              Cancel
            </button>
            <button
              onClick={confirmDuplicate}
              disabled={busy}
              data-testid="upload-transcript-confirm-dup"
              className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2">
              {busy ? <><Icons.Loader2 className="h-4 w-4 animate-spin" />Uploading…</> : "Continue Anyway"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Main upload form ───────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="upload-transcript-dialog">
        <DialogHeader>
          <DialogTitle>Upload Transcript</DialogTitle>
          <DialogDescription>
            Upload a meeting transcript to let Allison analyze the conversation and extract findings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <Field label="Meeting Title" required>
            <input
              data-testid="utd-title"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Tax Strategy Consultation"
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Meeting Date" required>
              <input
                type="date"
                data-testid="utd-date"
                value={form.meeting_date}
                onChange={(e) => set("meeting_date", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Meeting Time" required>
              <input
                type="time"
                data-testid="utd-time"
                value={form.meeting_time}
                onChange={(e) => set("meeting_time", e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Timezone" required>
              <select
                data-testid="utd-timezone"
                value={form.meeting_timezone}
                onChange={(e) => set("meeting_timezone", e.target.value)}
                className={selectCls}>
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Source / Platform" required>
              <select
                data-testid="utd-platform"
                value={form.source_platform}
                onChange={(e) => set("source_platform", e.target.value)}
                className={selectCls}>
                {PLATFORMS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Transcript File" required>
            <label
              className="flex items-center gap-3 border-2 border-dashed border-slate-200 rounded-lg px-4 py-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors group"
              data-testid="utd-file-label">
              <Icons.FileText className="h-5 w-5 text-slate-400 group-hover:text-blue-500 shrink-0" />
              <div className="flex-1 min-w-0">
                {file
                  ? <span className="text-sm font-medium text-slate-800 truncate block">{file.name}</span>
                  : <span className="text-sm text-slate-400">Click to select · .txt .vtt .srt .docx .pdf</span>}
              </div>
              {file && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="text-slate-400 hover:text-slate-600 shrink-0"
                  data-testid="utd-file-clear">
                  <Icons.X className="h-4 w-4" />
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPTED}
                data-testid="utd-file-input"
                className="sr-only"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </Field>

          <Field label="Notes (optional)">
            <textarea
              data-testid="utd-notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Any context about this meeting (optional)"
              rows={2}
              className={`${inputCls} resize-none`}
            />
          </Field>
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            data-testid="utd-submit"
            className="px-4 py-2 text-sm rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-700 disabled:opacity-50 flex items-center gap-2">
            {busy
              ? <><Icons.Loader2 className="h-4 w-4 animate-spin" />Uploading…</>
              : <><Icons.Upload className="h-4 w-4" />Upload Transcript</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
